import { useCallback, useEffect, useRef, useState } from 'react';

// Sustained reference tone; plays until stopped.
export function useReferenceTone() {
  const [playing, setPlaying] = useState(null);
  const ref = useRef({});

  const stop = useCallback(() => {
    const { ctx, osc, gain } = ref.current;
    if (osc && ctx) {
      gain.gain.setTargetAtTime(0, ctx.currentTime, 0.03);
      osc.stop(ctx.currentTime + 0.2);
    }
    ref.current = { ctx };
    setPlaying(null);
  }, []);

  const play = useCallback((key, freq) => {
    stop();
    let { ctx } = ref.current;
    if (!ctx) ctx = new (window.AudioContext || window.webkitAudioContext)();
    ctx.resume().catch(() => {});
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'triangle';
    osc.frequency.value = freq;
    gain.gain.value = 0;
    gain.gain.setTargetAtTime(0.35, ctx.currentTime, 0.03);
    osc.connect(gain).connect(ctx.destination);
    osc.start();
    ref.current = { ctx, osc, gain };
    setPlaying(key);
  }, [stop]);

  const toggle = useCallback((key, freq) => {
    if (playing === key) stop();
    else play(key, freq);
  }, [playing, play, stop]);

  useEffect(() => {
    const onVis = () => { if (document.hidden) stop(); };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      document.removeEventListener('visibilitychange', onVis);
      stop();
      ref.current.ctx?.close().catch(() => {});
    };
  }, [stop]);

  return { playing, toggle, play, stop };
}
